import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import AuthContext from "../Context/AuthContext";

// küçük debounce hook'u
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// isimden baş harf üretir
function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "U";
}

export default function YourTeam() {
  const history = useHistory();
  const location = useLocation();
  const { userData } = useContext(AuthContext);

  const user = userData?.user?.user;
  const role = user?.role || "User";

  // state
  const [members, setMembers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(true);
  const [err, setErr] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 200);

  // Sekmeler
  const tabs = [
    { key: "accounts", label: "Account", path: "/accounts" },
    { key: "guidelines", label: "Guidelines", path: "/guidelines" },
    { key: "yourteam", label: "Your Team", path: "/yourteam" },
  ];
  const activeKey =
    tabs.find((t) => location.pathname.startsWith(`/${t.key}`))?.key || "yourteam";

  // Üyeleri çek
  useEffect(() => {
    let mounted = true;
    async function fetchMembers() {
      if (!user?._id) return;
      try {
        setIsLoadingMembers(true);
        setErr(null);
        const { data } = await axios.post("https://act-api.vercel.app/members", {
          id: user._id,
        });
        if (!mounted) return;
        setMembers(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setErr("Takım üyeleri yüklenirken bir sorun oluştu. Lütfen tekrar deneyin.");
      } finally {
        mounted && setIsLoadingMembers(false);
      }
    }
    fetchMembers();
    return () => {
      mounted = false;
    };
  }, [user?._id]);

  // Adminleri çek
  useEffect(() => {
    let mounted = true;
    async function fetchAdmins() {
      try {
        setIsLoadingAdmins(true);
        setErr(null);
        const { data } = await axios.get("https://act-api.vercel.app/admins");
        if (!mounted) return;
        setAdmins(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setErr((prev) => prev ?? "Adminler yüklenirken bir sorun oluştu.");
      } finally {
        mounted && setIsLoadingAdmins(false);
      }
    }
    fetchAdmins();
    return () => {
      mounted = false;
    };
  }, []);

  // Filtreleme + sıralama
  const filteredMembers = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const base = members || [];
    const filtered = term
      ? base.filter(
          (m) =>
            (m?.name || "").toLowerCase().includes(term) ||
            (m?.eMail || "").toLowerCase().includes(term)
        )
      : base;
    return filtered.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [members, debouncedSearch]);

  const filteredAdmins = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const base = admins || [];
    const filtered = term
      ? base.filter(
          (m) =>
            (m?.name || "").toLowerCase().includes(term) ||
            (m?.eMail || "").toLowerCase().includes(term)
        )
      : base;
    return filtered.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [admins, debouncedSearch]);

  // navigation helpers
  function go(path) {
    history.push(path);
  }
  function openUser(id) {
    history.push("/userupdate/" + id);
  }

  const isLoading = isLoadingMembers || isLoadingAdmins;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Üst Bar */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between py-3 gap-3">
            {/* Sekmeler */}
            <div className="w-full sm:w-auto">
              <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
                {tabs.map((t) => {
                  const active = activeKey === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => go(t.path)}
                      className={[
                        "px-4 py-2 rounded-lg text-sm font-semibold transition",
                        active
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
                      ].join(" ")}
                      aria-current={active ? "page" : undefined}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Arama + Aksiyonlar */}
            <div className="flex w-full sm:w-auto items-center gap-2">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search team by name or email…"
                  aria-label="Search team"
                  className="w-full sm:w-80 rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </div>

              <button
                onClick={() => go("/newmember")}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 transition"
                title="Add member"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Member
              </button>

              {role === "Admin" && (
                <button
                  onClick={() => go("/newadmin")}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-4 focus:ring-slate-200 transition"
                  title="Add admin"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add Admin
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <header className="mb-6 flex items-end justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Your Team</h1>
            <p className="text-slate-500 text-sm">
              {debouncedSearch
                ? `Results for “${debouncedSearch}”`
                : "All members and admins in your workspace"}
            </p>
          </div>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
            {isLoading ? "…" : `${filteredMembers.length} members • ${filteredAdmins.length} admins`}
          </span>
        </header>

        {/* Hata */}
        {err && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* Yükleniyor iskeleti */}
        {(isLoadingMembers || isLoadingAdmins) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-slate-200 animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 w-3/5 bg-slate-200 rounded animate-pulse mb-2" />
                    <div className="h-3 w-2/5 bg-slate-200 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-9 mt-4 w-9 bg-slate-200 rounded-full self-end animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {/* MEMBERS */}
        {!isLoading && !err && (
          <>
            <h2 className="mt-2 mb-3 text-sm font-semibold text-slate-500 uppercase tracking-wider">
              Members
            </h2>

            {filteredMembers.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <h3 className="font-semibold">Üye bulunamadı</h3>
                <p className="text-sm text-slate-500">
                  Farklı bir arama yapabilir veya yeni bir üye ekleyebilirsiniz.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMembers.map(({ name, eMail, _id, imageURL }) => (
                  <article
                    key={_id}
                    className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative h-12 w-12">
                        {imageURL ? (
                          <img
                            src={imageURL}
                            alt={name || "User avatar"}
                            className="h-12 w-12 rounded-full object-cover ring-2 ring-slate-100"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const parent = e.currentTarget.parentElement;
                              if (parent && !parent.querySelector("span[data-fallback]")) {
                                const span = document.createElement("span");
                                span.setAttribute("data-fallback", "true");
                                span.className =
                                  "absolute inset-0 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-semibold";
                                span.textContent = initialsOf(name);
                                parent.appendChild(span);
                              }
                            }}
                          />
                        ) : (
                          <span className="absolute inset-0 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-semibold">
                            {initialsOf(name)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{name || "Unnamed"}</h3>
                        <p className="text-xs text-slate-500 truncate mt-1">{eMail || "—"}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                        Member
                      </span>
                      <button
                        onClick={() => openUser(_id)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200"
                        aria-label={`Open ${name || "user"}`}
                        title="Open details"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* ADMINS */}
            <h2 className="mt-10 mb-3 text-sm font-semibold text-slate-500 uppercase tracking-wider">
              Admins
            </h2>

            {filteredAdmins.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <h3 className="font-semibold">Admin bulunamadı</h3>
                <p className="text-sm text-slate-500">
                  Aramayı temizleyin veya yeni bir admin ekleyin.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAdmins.map(({ name, eMail, _id, imageURL }) => (
                  <article
                    key={_id}
                    className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative h-12 w-12">
                        {imageURL ? (
                          <img
                            src={imageURL}
                            alt={name || "Admin avatar"}
                            className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-100"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const parent = e.currentTarget.parentElement;
                              if (parent && !parent.querySelector("span[data-fallback]")) {
                                const span = document.createElement("span");
                                span.setAttribute("data-fallback", "true");
                                span.className =
                                  "absolute inset-0 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-semibold";
                                span.textContent = initialsOf(name);
                                parent.appendChild(span);
                              }
                            }}
                          />
                        ) : (
                          <span className="absolute inset-0 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-semibold">
                            {initialsOf(name)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{name || "Unnamed"}</h3>
                        <p className="text-xs text-slate-500 truncate mt-1">{eMail || "—"}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                        Admin
                      </span>
                      
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
